import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Clock, MapPin, Globe } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MarketingFooter } from './MarketingFooter';
import { cn } from '@/lib/utils';

export const PerfectBookingSkeleton = () => {
  return (
    <div className="h-screen bg-white">
      <Helmet>
        <title>Loading Booking Page | ChronoCal</title>
        <meta name="description" content="Loading meeting booking page..." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      
      {/* Loading indicator - hidden on mobile to avoid clutter */}
      <div className="fixed top-4 right-4 z-50 bg-slate-600 text-white px-3 py-1 rounded-full text-xs font-medium animate-pulse hidden lg:block">
        Loading...
      </div>
      
      <div className="h-screen flex flex-col">
        <div className="flex-1 flex flex-col">
          {/* Mobile layout - stacked */}
          <div className="block lg:hidden">
            {/* Left panel - mobile */}
            <div className="flex-shrink-0">
              <Card className="mx-auto w-full rounded-none border-none shadow-none">
                <CardContent className="p-0">
                  <div className="relative bg-slate-50 p-4 flex flex-col h-20">
                    {/* Background gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-slate-100 animate-pulse" />
                    
                    <div className="relative z-10 h-full flex flex-col justify-center">
                      {/* Clean mobile header layout */}
                      <div className="flex items-center justify-between">
                        {/* Left: Logo + Title */}
                        <div className="flex items-center space-x-3">
                          <Skeleton className="h-8 w-8 rounded-full bg-slate-200 animate-pulse" />
                          <div className="flex flex-col">
                            <Skeleton className="h-4 w-24 bg-slate-300 animate-pulse mb-1" />
                            <Skeleton className="h-3 w-32 bg-slate-400 animate-pulse" />
                          </div>
                        </div>
                        
                        {/* Right: Step indicator */}
                        <Skeleton className="h-6 w-16 bg-slate-300 animate-pulse rounded" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right panel - mobile */}
            <div className="flex-1 bg-white flex flex-col relative">
              {/* Sticky step navigation at the top */}
              <div className="w-full h-15 flex items-center justify-between sticky top-0 bg-white z-10 py-1 px-1">
                <div />
                <div className="text-sm text-gray-500 justify-end mr-3">
                  Step 1 of 3
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4">
                <div className="pb-4 max-w-4xl m-auto">
                  {/* Date picker skeleton */}
                  <div className="space-y-6">
                    {/* Calendar skeleton */}
                    <div className="flex flex-col space-y-4">
                      {/* Month navigation */}
                      <div className="flex items-center justify-between">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <Skeleton className="h-6 w-32" />
                        <Skeleton className="h-8 w-8 rounded-full" />
                      </div>
                      
                      {/* Day labels */}
                      <div className="grid grid-cols-7 gap-1 text-center text-sm">
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day, i) => (
                          <div key={i} className="h-8 flex items-center justify-center font-medium text-gray-500">
                            {day}
                          </div>
                        ))}
                      </div>
                      
                      {/* Calendar grid - 6 rows of dates */}
                      {Array.from({ length: 6 }).map((_, rowIndex) => (
                        <div key={rowIndex} className="grid grid-cols-7 gap-1">
                          {Array.from({ length: 7 }).map((_, colIndex) => {
                            // Simulate today's date (middle of current week)
                            const isToday = rowIndex === 2 && colIndex === 3; // Wednesday of 3rd week
                            const isSelected = rowIndex === 3 && colIndex === 1; // Tuesday of 4th week
                            
                            return (
                              <div 
                                key={`${rowIndex}-${colIndex}`} 
                                className={cn(
                                  "size-10 lg:size-9 rounded-full flex items-center justify-center text-sm font-bold",
                                  "bg-sky-100 border-zinc-100", // Default calendar day styling
                                  isToday && "bg-green-100", // Today styling
                                  isSelected && "bg-blue-600 text-white" // Selected styling
                                )}
                              >
                                <Skeleton className={cn(
                                  "h-6 w-6 rounded-full",
                                  isToday && "bg-green-200",
                                  isSelected && "bg-white/20"
                                )} />
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop layout */}
          <div className="hidden lg:flex flex-1 items-center justify-center p-4 min-h-0">
            <div className="w-full h-full min-h-0">
              <Card className="mx-auto w-full max-w-6xl border-none shadow-2xl overflow-hidden h-full">
                <CardContent className="p-0 h-full">
                  <div className="flex gap-0 h-full transition-all duration-75 ease-out min-h-0">
                    
                    {/* Left Column - SharedLeftColumn skeleton */}
                    <div className="transition-all duration-75 ease-out flex-shrink-0 w-1/2">
                      <div className="relative bg-gray-50 p-8 flex flex-col h-full">
                        {/* Background gradient */}
                        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-slate-100 animate-pulse" />
                        
                        <div className="relative z-10 h-full flex flex-col">
                          <div className="grid grid-cols-1 gap-2 h-full justify-between">
                            <div>
                              {/* Logo and Business Info */}
                              <div className="mb-2 flex justify-start">
                                <Skeleton className="h-10 w-10 rounded-full mr-3 bg-slate-200 animate-pulse" />
                                <Skeleton className="h-4 w-24 bg-slate-300 animate-pulse" />
                              </div>

                              {/* Meeting Details */}
                              <div className="mb-6 flex flex-col space-y-5">
                                {/* Meeting Title */}
                                <div>
                                  <Skeleton className="h-12 w-64 bg-slate-400 animate-pulse mb-4" />
                                </div>

                                {/* Description */}
                                <div className="hidden lg:flex">
                                  <Skeleton className="h-6 w-80 bg-slate-300 animate-pulse" />
                                </div>
                              </div>
                            </div>

                            {/* Selected Date/Time Display */}
                            <div className="flex flex-col justify-end">
                              {/* Meeting Details Section - Desktop */}
                              <div className="mb-4">
                                <div className="space-y-3">
                                  {/* Duration */}
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-white/60" />
                                    <Skeleton className="h-4 w-16 bg-white/80" />
                                  </div>

                                  {/* Location */}
                                  <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-white/60" />
                                    <Skeleton className="h-4 w-24 bg-white/80" />
                                  </div>

                                  {/* Timezone */}
                                  <div className="flex items-center gap-2">
                                    <Globe className="h-4 w-4 text-white/60" />
                                    <Skeleton className="h-4 w-20 bg-white/80" />
                                  </div>
                                </div>
                              </div>

                              {/* DateTime Display */}
                              <div className="bg-black/40 backdrop-blur-sm rounded-xl p-4">
                                <div className="flex items-center">
                                  <div className="bg-white rounded-lg p-3 mr-4">
                                    <Skeleton className="w-8 h-8" />
                                  </div>
                                  <div className="flex-1">
                                    <Skeleton className="h-6 w-32 bg-white mb-2" />
                                    <div className="flex items-center">
                                      <Skeleton className="h-5 w-5 mr-2 bg-white/60" />
                                      <Skeleton className="h-5 w-16 bg-white/60" />
                                    </div>
                                  </div>
                                  <div className="flex items-center">
                                    <Skeleton className="h-5 w-5 mr-2 bg-white/60" />
                                    <Skeleton className="h-5 w-12 bg-white/60" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Middle Column - DatePickerStep skeleton */}
                    <div className="p-6 pt-4 bg-white overflow-y-auto h-full border-r border-zinc-100 transition-all duration-75 ease-out flex-shrink-0 flex flex-col w-1/2">
                      <div className="space-y-6">
                        {/* Calendar skeleton */}
                        <div className="flex flex-col space-y-4">
                          {/* Month navigation */}
                          <div className="flex items-center justify-between">
                            <Skeleton className="h-8 w-8 rounded-full" />
                            <Skeleton className="h-6 w-32" />
                            <Skeleton className="h-8 w-8 rounded-full" />
                          </div>
                          
                          {/* Day labels */}
                          <div className="grid grid-cols-7 gap-1 text-center text-sm">
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day, i) => (
                              <div key={i} className="h-8 flex items-center justify-center font-medium text-gray-500">
                                {day}
                              </div>
                            ))}
                          </div>
                          
                          {/* Calendar grid - 6 rows of dates */}
                          {Array.from({ length: 6 }).map((_, rowIndex) => (
                            <div key={rowIndex} className="grid grid-cols-7 gap-1">
                              {Array.from({ length: 7 }).map((_, colIndex) => {
                                // Simulate today's date (middle of current week)
                                const isToday = rowIndex === 2 && colIndex === 3; // Wednesday of 3rd week
                                const isSelected = rowIndex === 3 && colIndex === 1; // Tuesday of 4th week
                                
                                return (
                                  <div 
                                    key={`${rowIndex}-${colIndex}`} 
                                    className={cn(
                                      "size-10 lg:size-9 rounded-full flex items-center justify-center text-sm font-bold",
                                      "bg-sky-100 border-zinc-100", // Default calendar day styling
                                      isToday && "bg-green-100", // Today styling
                                      isSelected && "bg-blue-600 text-white" // Selected styling
                                    )}
                                  >
                                    <Skeleton className={cn(
                                      "h-6 w-6 rounded-full",
                                      isToday && "bg-green-200",
                                      isSelected && "bg-white/20"
                                    )} />
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Right Column - TimeSlotStep skeleton (hidden on date step) */}
                    <div className="hidden w-0 p-6 pt-4 bg-white h-full transition-all duration-75 ease-out flex-shrink-0 min-h-0">
                      {/* This will be shown when time slots are selected */}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <MarketingFooter meetingLinkId="test" />
      </div>
    </div>
  );
};
